export const INDUSTRY_DATASET_STATE_STORAGE_KEY = 'analyticsDatasetState:v2';
export const INDUSTRY_DATASET_STATE_EVENT_NAME = 'analyticsDatasetStateChange';

export const INDUSTRY_DATASET_IDS = {
  OPERATING_KPIS: 'operating-kpis',
  JOB_POSTINGS: 'job-postings',
  GUIDANCE: 'guidance',
  FILING_CHANGES: 'filing-changes',
  PARTNER_TRENDS: 'partner-trends',
  DEVELOPER_SIGNALS: 'developer-signals',
  TECH_STACK: 'tech-stack',
};

export const INDUSTRY_DATASET_LAYER_IDS = {
  MARKET: 'market',
  CATEGORY: 'category',
  VENDOR: 'vendor',
  PROJECT: 'project',
  COMPANY: 'company',
  PROJECT_DETAIL: 'project-detail',
  VIEW: 'view',
};

export const INDUSTRY_DATASET_FILTER_SCOPE_IDS = {
  CATEGORY: 'category',
  VENDOR: 'vendor',
  COMPANY: 'company',
  MARKET: 'market',
};

export const DEFAULT_INDUSTRY_COMPANY_TICKER = 'SNOW';

const DEFAULT_STATE = {
  version: 2,
  filtersByDepth: {},
  layers: {},
};

let cachedStateRaw = null;
let cachedStateSnapshot = DEFAULT_STATE;

const getStorage = () => {
  if (typeof window === 'undefined') return null;
  return window.localStorage || null;
};

export const normalizeIndustryTicker = (ticker) => {
  return normalizeIndustryTickers(ticker)[0];
};

export const normalizeIndustryTickers = (tickers) => {
  const rawTickers = Array.isArray(tickers)
    ? tickers
    : typeof tickers === 'string'
      ? tickers.split(',')
      : tickers
        ? [tickers]
        : [];
  const seen = new Set();
  return rawTickers
    .map((ticker) => (
      typeof ticker === 'object'
        ? String(ticker?.ticker || '').trim().toUpperCase()
        : String(ticker || '').trim().toUpperCase()
    ))
    .filter((ticker) => {
      if (!ticker || seen.has(ticker)) return false;
      seen.add(ticker);
      return true;
    });
};

export const normalizeIndustryCategories = (categories) => {
  if (Array.isArray(categories)) {
    return categories.map((category) => String(category || '').trim()).filter(Boolean);
  }

  if (typeof categories === 'string') {
    return categories
      .split(',')
      .map((category) => category.trim())
      .filter(Boolean);
  }

  return [];
};

export const getIndustryDatasetFilterScopeId = (layerId) => {
  switch (layerId) {
    case INDUSTRY_DATASET_LAYER_IDS.CATEGORY:
      return INDUSTRY_DATASET_FILTER_SCOPE_IDS.CATEGORY;
    case INDUSTRY_DATASET_LAYER_IDS.VENDOR:
    case INDUSTRY_DATASET_LAYER_IDS.PROJECT:
      return INDUSTRY_DATASET_FILTER_SCOPE_IDS.VENDOR;
    case INDUSTRY_DATASET_LAYER_IDS.COMPANY:
    case INDUSTRY_DATASET_LAYER_IDS.PROJECT_DETAIL:
      return INDUSTRY_DATASET_FILTER_SCOPE_IDS.COMPANY;
    case INDUSTRY_DATASET_LAYER_IDS.MARKET:
      return INDUSTRY_DATASET_FILTER_SCOPE_IDS.MARKET;
    default:
      return layerId || undefined;
  }
};

const normalizeScopedFilters = (scopeId, filters = {}) => {
  const normalized = {};
  if (scopeId === INDUSTRY_DATASET_FILTER_SCOPE_IDS.CATEGORY) {
    if (Object.prototype.hasOwnProperty.call(filters, 'category')) {
      normalized.category = normalizeIndustryCategories(filters.category);
    }
    return normalized;
  }

  if (scopeId === INDUSTRY_DATASET_FILTER_SCOPE_IDS.VENDOR) {
    if (Object.prototype.hasOwnProperty.call(filters, 'category')) {
      normalized.category = normalizeIndustryCategories(filters.category);
    }
    if (Object.prototype.hasOwnProperty.call(filters, 'ticker')) {
      normalized.ticker = normalizeIndustryTickers(filters.ticker);
    }
    return normalized;
  }

  if (scopeId === INDUSTRY_DATASET_FILTER_SCOPE_IDS.COMPANY) {
    if (Object.prototype.hasOwnProperty.call(filters, 'ticker')) {
      normalized.ticker = normalizeIndustryTickers(filters.ticker).slice(0, 1);
    }
    return normalized;
  }

  return normalized;
};

const viewIdFromNavView = (layerId, navViewId) => {
  const value = String(navViewId || '');
  const prefix = `${layerId}-`;
  return value.startsWith(prefix) ? value.slice(prefix.length) : value || undefined;
};

const appendCompanyTickerQuery = (path, ticker) => {
  const normalizedTicker = normalizeIndustryTicker(ticker);
  if (!normalizedTicker) return path;
  const params = new URLSearchParams();
  params.set('ticker', normalizedTicker);
  return `${path}?${params.toString()}`;
};

const DATASET_REGISTRY = {
  [INDUSTRY_DATASET_IDS.OPERATING_KPIS]: {
    id: INDUSTRY_DATASET_IDS.OPERATING_KPIS,
    supportedLayers: [
      INDUSTRY_DATASET_LAYER_IDS.CATEGORY,
      INDUSTRY_DATASET_LAYER_IDS.VENDOR,
      INDUSTRY_DATASET_LAYER_IDS.COMPANY,
    ],
    defaultLayerId: INDUSTRY_DATASET_LAYER_IDS.VENDOR,
    fallbackLayers: [
      INDUSTRY_DATASET_LAYER_IDS.VENDOR,
      INDUSTRY_DATASET_LAYER_IDS.CATEGORY,
      INDUSTRY_DATASET_LAYER_IDS.COMPANY,
    ],
    fallbackLayerByRequested: {
      [INDUSTRY_DATASET_LAYER_IDS.MARKET]: INDUSTRY_DATASET_LAYER_IDS.CATEGORY,
      [INDUSTRY_DATASET_LAYER_IDS.PROJECT]: INDUSTRY_DATASET_LAYER_IDS.VENDOR,
      [INDUSTRY_DATASET_LAYER_IDS.PROJECT_DETAIL]: INDUSTRY_DATASET_LAYER_IDS.VENDOR,
    },
    defaultViewsByLayer: {
      [INDUSTRY_DATASET_LAYER_IDS.CATEGORY]: 'trends',
      [INDUSTRY_DATASET_LAYER_IDS.VENDOR]: 'table',
      [INDUSTRY_DATASET_LAYER_IDS.COMPANY]: 'financial-overview',
    },
    sharedFiltersByLayer: {
      [INDUSTRY_DATASET_LAYER_IDS.VENDOR]: ['category', 'ticker'],
      [INDUSTRY_DATASET_LAYER_IDS.COMPANY]: ['ticker'],
    },
    buildPath: ({ layerId, viewId }) => `/kpis/${layerId}/${viewId}`,
  },
  [INDUSTRY_DATASET_IDS.JOB_POSTINGS]: {
    id: INDUSTRY_DATASET_IDS.JOB_POSTINGS,
    supportedLayers: [
      INDUSTRY_DATASET_LAYER_IDS.CATEGORY,
      INDUSTRY_DATASET_LAYER_IDS.VENDOR,
      INDUSTRY_DATASET_LAYER_IDS.COMPANY,
    ],
    defaultLayerId: INDUSTRY_DATASET_LAYER_IDS.VENDOR,
    fallbackLayers: [
      INDUSTRY_DATASET_LAYER_IDS.VENDOR,
      INDUSTRY_DATASET_LAYER_IDS.CATEGORY,
      INDUSTRY_DATASET_LAYER_IDS.COMPANY,
    ],
    fallbackLayerByRequested: {
      [INDUSTRY_DATASET_LAYER_IDS.MARKET]: INDUSTRY_DATASET_LAYER_IDS.CATEGORY,
      [INDUSTRY_DATASET_LAYER_IDS.PROJECT]: INDUSTRY_DATASET_LAYER_IDS.VENDOR,
      [INDUSTRY_DATASET_LAYER_IDS.PROJECT_DETAIL]: INDUSTRY_DATASET_LAYER_IDS.COMPANY,
    },
    defaultViewsByLayer: {
      [INDUSTRY_DATASET_LAYER_IDS.CATEGORY]: 'trend',
      [INDUSTRY_DATASET_LAYER_IDS.VENDOR]: 'trend',
      [INDUSTRY_DATASET_LAYER_IDS.COMPANY]: 'trend',
    },
    sharedFiltersByLayer: {
      [INDUSTRY_DATASET_LAYER_IDS.CATEGORY]: ['category'],
      [INDUSTRY_DATASET_LAYER_IDS.VENDOR]: ['category', 'ticker'],
      [INDUSTRY_DATASET_LAYER_IDS.COMPANY]: ['ticker'],
    },
    buildPath: ({ layerId, viewId, ticker }) => (
      layerId === INDUSTRY_DATASET_LAYER_IDS.COMPANY
        ? appendCompanyTickerQuery(`/hiring/${layerId}/${viewId}`, ticker)
        : `/hiring/${layerId}/${viewId}`
    ),
  },
  [INDUSTRY_DATASET_IDS.GUIDANCE]: {
    id: INDUSTRY_DATASET_IDS.GUIDANCE,
    supportedLayers: [
      INDUSTRY_DATASET_LAYER_IDS.CATEGORY,
      INDUSTRY_DATASET_LAYER_IDS.VENDOR,
      INDUSTRY_DATASET_LAYER_IDS.COMPANY,
    ],
    defaultLayerId: INDUSTRY_DATASET_LAYER_IDS.VENDOR,
    fallbackLayers: [
      INDUSTRY_DATASET_LAYER_IDS.VENDOR,
      INDUSTRY_DATASET_LAYER_IDS.CATEGORY,
      INDUSTRY_DATASET_LAYER_IDS.COMPANY,
    ],
    fallbackLayerByRequested: {
      [INDUSTRY_DATASET_LAYER_IDS.MARKET]: INDUSTRY_DATASET_LAYER_IDS.CATEGORY,
      [INDUSTRY_DATASET_LAYER_IDS.PROJECT]: INDUSTRY_DATASET_LAYER_IDS.VENDOR,
      [INDUSTRY_DATASET_LAYER_IDS.PROJECT_DETAIL]: INDUSTRY_DATASET_LAYER_IDS.COMPANY,
    },
    defaultViewsByLayer: {
      [INDUSTRY_DATASET_LAYER_IDS.CATEGORY]: 'results',
      [INDUSTRY_DATASET_LAYER_IDS.VENDOR]: 'results',
      [INDUSTRY_DATASET_LAYER_IDS.COMPANY]: 'results',
    },
    sharedFiltersByLayer: {
      [INDUSTRY_DATASET_LAYER_IDS.CATEGORY]: ['category'],
      [INDUSTRY_DATASET_LAYER_IDS.VENDOR]: ['category', 'ticker'],
      [INDUSTRY_DATASET_LAYER_IDS.COMPANY]: ['ticker'],
    },
    buildPath: ({ layerId, viewId, ticker }) => {
      const integratedView = viewId === 'revisions' ? 'guidance-revisions' : 'guidance-results';
      return layerId === INDUSTRY_DATASET_LAYER_IDS.COMPANY
        ? appendCompanyTickerQuery(`/kpis/${layerId}/${integratedView}`, ticker)
        : `/kpis/${layerId}/${integratedView}`;
    },
  },
  [INDUSTRY_DATASET_IDS.PARTNER_TRENDS]: {
    id: INDUSTRY_DATASET_IDS.PARTNER_TRENDS,
    supportedLayers: [
      INDUSTRY_DATASET_LAYER_IDS.MARKET,
      INDUSTRY_DATASET_LAYER_IDS.CATEGORY,
      INDUSTRY_DATASET_LAYER_IDS.VENDOR,
      INDUSTRY_DATASET_LAYER_IDS.COMPANY,
    ],
    defaultLayerId: INDUSTRY_DATASET_LAYER_IDS.VENDOR,
    fallbackLayers: [
      INDUSTRY_DATASET_LAYER_IDS.VENDOR,
      INDUSTRY_DATASET_LAYER_IDS.CATEGORY,
      INDUSTRY_DATASET_LAYER_IDS.MARKET,
      INDUSTRY_DATASET_LAYER_IDS.COMPANY,
    ],
    fallbackLayerByRequested: {
      [INDUSTRY_DATASET_LAYER_IDS.PROJECT]: INDUSTRY_DATASET_LAYER_IDS.VENDOR,
      [INDUSTRY_DATASET_LAYER_IDS.PROJECT_DETAIL]: INDUSTRY_DATASET_LAYER_IDS.COMPANY,
    },
    defaultViewsByLayer: {
      [INDUSTRY_DATASET_LAYER_IDS.MARKET]: 'trends',
      [INDUSTRY_DATASET_LAYER_IDS.CATEGORY]: 'trends',
      [INDUSTRY_DATASET_LAYER_IDS.VENDOR]: 'trends',
      [INDUSTRY_DATASET_LAYER_IDS.COMPANY]: 'dashboard',
    },
    sharedFiltersByLayer: {
      [INDUSTRY_DATASET_LAYER_IDS.MARKET]: [],
      [INDUSTRY_DATASET_LAYER_IDS.CATEGORY]: ['category'],
      [INDUSTRY_DATASET_LAYER_IDS.VENDOR]: ['category', 'ticker'],
      [INDUSTRY_DATASET_LAYER_IDS.COMPANY]: ['ticker'],
    },
    buildPath: ({ layerId, viewId, ticker }) => {
      const integratedView = layerId === INDUSTRY_DATASET_LAYER_IDS.COMPANY && viewId === 'postings'
        ? 'partner-jobs'
        : 'partner-trends';
      return layerId === INDUSTRY_DATASET_LAYER_IDS.COMPANY
        ? appendCompanyTickerQuery(`/hiring/${layerId}/${integratedView}`, ticker)
        : `/hiring/${layerId}/${integratedView}`;
    },
  },
  [INDUSTRY_DATASET_IDS.FILING_CHANGES]: {
    id: INDUSTRY_DATASET_IDS.FILING_CHANGES,
    supportedLayers: [
      INDUSTRY_DATASET_LAYER_IDS.VENDOR,
      INDUSTRY_DATASET_LAYER_IDS.COMPANY,
    ],
    defaultLayerId: INDUSTRY_DATASET_LAYER_IDS.VENDOR,
    fallbackLayers: [
      INDUSTRY_DATASET_LAYER_IDS.VENDOR,
      INDUSTRY_DATASET_LAYER_IDS.COMPANY,
    ],
    fallbackLayerByRequested: {
      [INDUSTRY_DATASET_LAYER_IDS.MARKET]: INDUSTRY_DATASET_LAYER_IDS.VENDOR,
      [INDUSTRY_DATASET_LAYER_IDS.CATEGORY]: INDUSTRY_DATASET_LAYER_IDS.VENDOR,
      [INDUSTRY_DATASET_LAYER_IDS.PROJECT]: INDUSTRY_DATASET_LAYER_IDS.VENDOR,
      [INDUSTRY_DATASET_LAYER_IDS.PROJECT_DETAIL]: INDUSTRY_DATASET_LAYER_IDS.COMPANY,
    },
    defaultViewsByLayer: {
      [INDUSTRY_DATASET_LAYER_IDS.VENDOR]: 'events',
      [INDUSTRY_DATASET_LAYER_IDS.COMPANY]: 'events',
    },
    sharedFiltersByLayer: {
      [INDUSTRY_DATASET_LAYER_IDS.VENDOR]: ['category', 'ticker'],
      [INDUSTRY_DATASET_LAYER_IDS.COMPANY]: ['ticker'],
    },
    buildPath: ({ layerId, viewId, ticker }) => (
      layerId === INDUSTRY_DATASET_LAYER_IDS.COMPANY
        ? appendCompanyTickerQuery(`/filingchanges/${layerId}/${viewId}`, ticker)
        : `/filingchanges/${layerId}/${viewId}`
    ),
  },
  [INDUSTRY_DATASET_IDS.DEVELOPER_SIGNALS]: {
    id: INDUSTRY_DATASET_IDS.DEVELOPER_SIGNALS,
    supportedLayers: [
      INDUSTRY_DATASET_LAYER_IDS.CATEGORY,
      INDUSTRY_DATASET_LAYER_IDS.PROJECT,
      INDUSTRY_DATASET_LAYER_IDS.PROJECT_DETAIL,
    ],
    defaultLayerId: INDUSTRY_DATASET_LAYER_IDS.PROJECT,
    fallbackLayers: [
      INDUSTRY_DATASET_LAYER_IDS.PROJECT,
      INDUSTRY_DATASET_LAYER_IDS.CATEGORY,
      INDUSTRY_DATASET_LAYER_IDS.PROJECT_DETAIL,
    ],
    fallbackLayerByRequested: {
      [INDUSTRY_DATASET_LAYER_IDS.MARKET]: INDUSTRY_DATASET_LAYER_IDS.CATEGORY,
      [INDUSTRY_DATASET_LAYER_IDS.COMPANY]: INDUSTRY_DATASET_LAYER_IDS.PROJECT_DETAIL,
      [INDUSTRY_DATASET_LAYER_IDS.VIEW]: INDUSTRY_DATASET_LAYER_IDS.PROJECT,
    },
    layerAliases: {
      [INDUSTRY_DATASET_LAYER_IDS.VENDOR]: INDUSTRY_DATASET_LAYER_IDS.PROJECT,
    },
    defaultViewsByLayer: {
      [INDUSTRY_DATASET_LAYER_IDS.CATEGORY]: 'trends',
      [INDUSTRY_DATASET_LAYER_IDS.PROJECT]: 'trends',
      [INDUSTRY_DATASET_LAYER_IDS.PROJECT_DETAIL]: 'packages',
    },
    sharedFiltersByLayer: {
      [INDUSTRY_DATASET_LAYER_IDS.CATEGORY]: ['category'],
      [INDUSTRY_DATASET_LAYER_IDS.PROJECT]: ['category', 'ticker'],
      [INDUSTRY_DATASET_LAYER_IDS.PROJECT_DETAIL]: ['ticker'],
    },
    buildPath: ({ layerId, viewId, ticker, projectId }) => {
      const path = `/adoption/${layerId}/${viewId}`;
      const normalizedTicker = normalizeIndustryTicker(ticker);
      const normalizedProjectId = String(projectId || '').trim();
      if (layerId !== INDUSTRY_DATASET_LAYER_IDS.PROJECT_DETAIL) {
        return path;
      }

      const params = new URLSearchParams();
      if (normalizedTicker) params.set('ticker', normalizedTicker);
      if (normalizedProjectId) params.set('projectId', normalizedProjectId);
      const query = params.toString();
      return `${path}${query ? `?${query}` : ''}`;
    },
  },
  [INDUSTRY_DATASET_IDS.TECH_STACK]: {
    id: INDUSTRY_DATASET_IDS.TECH_STACK,
    supportedLayers: [INDUSTRY_DATASET_LAYER_IDS.VIEW],
    defaultLayerId: INDUSTRY_DATASET_LAYER_IDS.VIEW,
    fallbackLayers: [INDUSTRY_DATASET_LAYER_IDS.VIEW],
    defaultViewsByLayer: {
      [INDUSTRY_DATASET_LAYER_IDS.VIEW]: undefined,
    },
    sharedFiltersByLayer: {
      [INDUSTRY_DATASET_LAYER_IDS.VIEW]: [],
    },
    buildPath: () => '/techstack',
  },
};

export const getIndustryDatasetRegistry = () => DATASET_REGISTRY;

export const getIndustryDatasetConfig = (datasetId) => DATASET_REGISTRY[datasetId] || null;

export const readIndustryDatasetState = () => {
  const storage = getStorage();
  if (!storage) return DEFAULT_STATE;

  try {
    const raw = storage.getItem(INDUSTRY_DATASET_STATE_STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return DEFAULT_STATE;
    return {
      ...DEFAULT_STATE,
      ...parsed,
      version: 2,
      filtersByDepth: parsed.filtersByDepth && typeof parsed.filtersByDepth === 'object' ? parsed.filtersByDepth : {},
      layers: parsed.layers && typeof parsed.layers === 'object' ? parsed.layers : {},
    };
  } catch (error) {
    return DEFAULT_STATE;
  }
};

export const getIndustryDatasetStateSnapshot = () => {
  const storage = getStorage();
  if (!storage) return DEFAULT_STATE;

  try {
    const raw = storage.getItem(INDUSTRY_DATASET_STATE_STORAGE_KEY) || '';
    if (raw === cachedStateRaw) return cachedStateSnapshot;
    cachedStateRaw = raw;
    cachedStateSnapshot = raw ? readIndustryDatasetState() : DEFAULT_STATE;
    return cachedStateSnapshot;
  } catch {
    return DEFAULT_STATE;
  }
};

export const getIndustryDatasetEffectiveFilters = ({
  layerId,
  state = readIndustryDatasetState(),
} = {}) => {
  const scopeId = getIndustryDatasetFilterScopeId(layerId);
  const filters = scopeId ? state.filtersByDepth?.[scopeId] || {} : {};
  const storedTickers = normalizeIndustryTickers(filters.ticker);
  const ticker = scopeId === INDUSTRY_DATASET_FILTER_SCOPE_IDS.COMPANY
    && storedTickers.length === 0
    ? [DEFAULT_INDUSTRY_COMPANY_TICKER]
    : storedTickers;

  return {
    category: normalizeIndustryCategories(filters.category),
    ticker,
  };
};

export const subscribeIndustryDatasetState = (listener) => {
  if (typeof window === 'undefined' || typeof listener !== 'function') {
    return () => {};
  }

  window.addEventListener(INDUSTRY_DATASET_STATE_EVENT_NAME, listener);
  window.addEventListener('storage', listener);

  return () => {
    window.removeEventListener(INDUSTRY_DATASET_STATE_EVENT_NAME, listener);
    window.removeEventListener('storage', listener);
  };
};

export const writeIndustryDatasetState = (state) => {
  const storage = getStorage();
  if (!storage) return;

  try {
    const nextState = {
      ...DEFAULT_STATE,
      ...state,
      version: 2,
    };
    const nextRaw = JSON.stringify(nextState);
    const currentRaw = storage.getItem(INDUSTRY_DATASET_STATE_STORAGE_KEY) || '';
    if (nextRaw === currentRaw) return;
    storage.setItem(INDUSTRY_DATASET_STATE_STORAGE_KEY, nextRaw);
    cachedStateRaw = nextRaw;
    cachedStateSnapshot = nextState;
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(INDUSTRY_DATASET_STATE_EVENT_NAME, { detail: nextState }));
    }
  } catch (error) {
    // Local storage can fail in private mode or when quota is exceeded.
  }
};

export const persistIndustryDatasetState = ({
  datasetId,
  layerId,
  viewId,
  filters = {},
  state = readIndustryDatasetState(),
} = {}) => {
  if (!datasetId || !layerId) return state;

  const scopeId = getIndustryDatasetFilterScopeId(layerId);
  const normalizedScopedFilters = normalizeScopedFilters(scopeId, filters);
  const existingScopeFilters = state.filtersByDepth?.[scopeId] || {};
  const nextScopeFilters = {
    ...existingScopeFilters,
    ...normalizedScopedFilters,
  };
  const nextFiltersByDepth = {
    ...(state.filtersByDepth || {}),
    ...(scopeId ? { [scopeId]: nextScopeFilters } : {}),
  };
  const nextViewByDataset = {
    ...(state.layers?.[scopeId]?.viewByDataset || {}),
    ...(viewId ? { [datasetId]: viewId } : {}),
  };
  const existingLayer = state.layers?.[scopeId] || {};
  const hasSameFiltersByDepth = JSON.stringify(nextFiltersByDepth) === JSON.stringify(state.filtersByDepth || {});
  const hasSameViewState = JSON.stringify(nextViewByDataset) === JSON.stringify(existingLayer.viewByDataset || {});
  if (
    hasSameFiltersByDepth
    && hasSameViewState
    && existingLayer.lastDatasetId === datasetId
  ) {
    return state;
  }

  const nextState = {
    ...DEFAULT_STATE,
    ...state,
    filtersByDepth: nextFiltersByDepth,
    layers: {
      ...(state.layers || {}),
      [scopeId]: {
        ...(state.layers?.[scopeId] || {}),
        lastDatasetId: datasetId,
        updatedAt: Date.now(),
        viewByDataset: nextViewByDataset,
      },
    },
  };

  writeIndustryDatasetState(nextState);
  return nextState;
};

export const resolveIndustryDatasetLayer = (datasetId, requestedLayerId) => {
  const config = getIndustryDatasetConfig(datasetId);
  if (!config) return requestedLayerId;

  const aliasedLayerId = config.layerAliases?.[requestedLayerId] || requestedLayerId;
  if (config.supportedLayers.includes(aliasedLayerId)) return aliasedLayerId;

  const fallbackLayer = config.fallbackLayerByRequested?.[requestedLayerId];
  if (fallbackLayer && config.supportedLayers.includes(fallbackLayer)) return fallbackLayer;

  return config.fallbackLayers.find((layerId) => config.supportedLayers.includes(layerId))
    || config.defaultLayerId;
};

export const buildIndustryDatasetPath = ({
  targetDatasetId,
  activeLayerId,
  viewId,
  filters = {},
  projectId,
  includeCommonFilters = true,
  state = readIndustryDatasetState(),
} = {}) => {
  const config = getIndustryDatasetConfig(targetDatasetId);
  if (!config) return undefined;

  const layerId = resolveIndustryDatasetLayer(targetDatasetId, activeLayerId || config.defaultLayerId);
  const scopeId = getIndustryDatasetFilterScopeId(layerId);
  const storedLayerState = state.layers?.[scopeId] || {};
  const targetViewId = viewId
    || storedLayerState.viewByDataset?.[targetDatasetId]
    || config.defaultViewsByLayer[layerId];
  const providedFilters = normalizeScopedFilters(scopeId, filters);
  const storedFilters = getIndustryDatasetEffectiveFilters({ layerId, state });
  const hasProvidedTickerFilter = Object.prototype.hasOwnProperty.call(filters, 'ticker');
  const rawProvidedTickers = hasProvidedTickerFilter ? normalizeIndustryTickers(filters.ticker) : [];
  const providedTickers = normalizeIndustryTickers(providedFilters.ticker);
  const storedTickers = storedFilters.ticker;
  const providedTicker = hasProvidedTickerFilter
    ? (rawProvidedTickers.length === 1 ? rawProvidedTickers[0] : undefined)
    : providedTickers[0];
  const storedTicker = storedTickers.length === 1 ? storedTickers[0] : undefined;
  const effectiveTicker = providedTicker
    || (
      scopeId === INDUSTRY_DATASET_FILTER_SCOPE_IDS.COMPANY
        ? storedTicker
        : includeCommonFilters && !hasProvidedTickerFilter
          ? storedTicker
          : undefined
    );

  return config.buildPath({
    layerId,
    viewId: targetViewId,
    ticker: effectiveTicker,
    projectId,
  });
};

export const buildIndustryDatasetNavPath = ({
  targetDatasetId,
  layerId,
  navViewId,
  filters,
  projectId,
  includeCommonFilters,
  state,
} = {}) => (
  buildIndustryDatasetPath({
    targetDatasetId,
    activeLayerId: layerId,
    viewId: viewIdFromNavView(layerId, navViewId),
    filters,
    projectId,
    includeCommonFilters,
    state,
  })
);
